/**
 * Unified Documents List
 * - Üst kısımda "Kullanıcı Sekmeleri" (Süper Admin / Admin için):
 *   "Herkes" + "Kendi Belgelerim" + her alt kullanıcı için ayrı sekme.
 *   Tıklayınca o kullanıcının belgeleri filtrelenir.
 * - Gelişmiş filtreler: arama, kaynak (manuel/avans), kategori, tarih aralığı,
 *   yıl, ay, işletme, para birimi, proje, tutar aralığı.
 */
import React, { useState, useEffect, useMemo, useRef } from 'react';
import axios from 'axios';
import {
  Search, FileText, Download, Eye, Filter, RefreshCw, Tag, Calendar, User as UserIcon,
  Loader2, SlidersHorizontal, X, DollarSign, Briefcase, Coins, Users, Send, History,
  ExternalLink, Copy,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../App';
import DocumentPreviewModal from './DocumentPreviewModal';
import AuditShareModal from './AuditShareModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ADMIN_ROLES = ['Super Admin', 'Süper Admin'];
const TR_MONTHS = [
  '', 'Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran',
  'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık',
];

const sourceBadge = (source) =>
  source === 'advance'
    ? 'bg-amber-50 text-amber-700 border border-amber-200'
    : 'bg-sky-50 text-sky-700 border border-sky-200';

const formatDate = (d) => {
  if (!d) return '-';
  try {
    const dt = new Date(d);
    if (isNaN(dt.getTime())) return String(d).slice(0, 10);
    return dt.toLocaleDateString('tr-TR', { year: 'numeric', month: '2-digit', day: '2-digit' });
  } catch {
    return String(d);
  }
};

const formatAmount = (amount, currency) => {
  if (amount === null || amount === undefined || amount === '') return '-';
  const n = Number(amount);
  if (isNaN(n)) return `${amount} ${currency || ''}`.trim();
  return `${n.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${currency || ''}`.trim();
};

const defaultAdvanced = {
  dateFrom: '',
  dateTo: '',
  year: 'all',
  month: 'all',
  supplier: 'all',
  currency: 'all',
  project: 'all',
  amountMin: '',
  amountMax: '',
};

const UnifiedDocumentsList = () => {
  const { user } = useAuth();
  // Solo-plan users have no "Herkes" concept (single-user tier) — hide the
  // user-scope tabs entirely so the page goes straight to the user's own
  // documents. Other plans keep the Süper Admin / Admin multi-user view.
  const isSoloPlan = (user?.company?.subscription_plan || '').toLowerCase() === 'solo';
  const isAdminLike = useMemo(
    () => !isSoloPlan && !!(user?.role?.name && ADMIN_ROLES.includes(user.role.name)),
    [user, isSoloPlan]
  );

  const [loading, setLoading] = useState(true);
  const [documents, setDocuments] = useState([]);
  const [facets, setFacets] = useState({
    categories: [], suppliers: [], currencies: [], projects: [], uploaders: [], years: [], months: [],
  });
  const [totalUnfiltered, setTotalUnfiltered] = useState(0);

  // User-tab state — 'all' | 'me' | <user_id>
  const [userTab, setUserTab] = useState('all');

  const [search, setSearch] = useState('');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [advanced, setAdvanced] = useState(defaultAdvanced);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [previewItem, setPreviewItem] = useState(null);
  const [showAuditModal, setShowAuditModal] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [auditHistory, setAuditHistory] = useState([]);
  const [showSchedules, setShowSchedules] = useState(false);
  const [schedules, setSchedules] = useState([]);
  const isMountedRef = useRef(false);

  const uploadedByParam = useMemo(() => {
    if (userTab === 'all') return '';
    if (userTab === 'me') return user?.id || '';
    return userTab;
  }, [userTab, user]);

  const fetchDocs = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (sourceFilter !== 'all') params.append('source', sourceFilter);
      if (categoryFilter !== 'all') params.append('category', categoryFilter);
      if (search.trim()) params.append('search', search.trim());
      if (uploadedByParam) params.append('uploaded_by', uploadedByParam);
      if (advanced.dateFrom) params.append('date_from', advanced.dateFrom);
      if (advanced.dateTo) params.append('date_to', advanced.dateTo);
      if (advanced.year !== 'all') params.append('year', advanced.year);
      if (advanced.month !== 'all') params.append('month', advanced.month);
      if (advanced.supplier !== 'all') params.append('supplier', advanced.supplier);
      if (advanced.currency !== 'all') params.append('currency', advanced.currency);
      if (advanced.project !== 'all') params.append('project', advanced.project);
      if (advanced.amountMin !== '') params.append('amount_min', advanced.amountMin);
      if (advanced.amountMax !== '') params.append('amount_max', advanced.amountMax);

      const res = await axios.get(`${API}/documents/all-unified?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.data?.success) {
        setDocuments(res.data.documents || []);
        const f = res.data.facets || {};
        setFacets({
          categories: f.categories || res.data.categories || [],
          suppliers: f.suppliers || [],
          currencies: f.currencies || [],
          projects: f.projects || [],
          uploaders: f.uploaders || [],
          years: f.years || [],
          months: f.months || [],
        });
        setTotalUnfiltered(res.data.total_unfiltered || res.data.total || 0);
      } else {
        toast.error('Belgeler yüklenemedi');
      }
    } catch (err) {
      console.error('❌ Unified docs fetch error:', err);
      toast.error(err.response?.data?.detail || 'Belgeler yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const delay = isMountedRef.current ? 350 : 0;
    isMountedRef.current = true;
    const t = setTimeout(fetchDocs, delay);
    return () => clearTimeout(t);
  }, [
    sourceFilter, categoryFilter, search, userTab,
    advanced.dateFrom, advanced.dateTo, advanced.year, advanced.month,
    advanced.supplier, advanced.currency, advanced.project,
    advanced.amountMin, advanced.amountMax,
  ]);

  const counts = useMemo(() => {
    const manual = documents.filter((d) => d.source === 'manual').length;
    const advance = documents.filter((d) => d.source === 'advance').length;
    return { total: documents.length, manual, advance };
  }, [documents]);

  const activeAdvancedCount = useMemo(() => {
    let n = 0;
    if (advanced.dateFrom) n++;
    if (advanced.dateTo) n++;
    if (advanced.year !== 'all') n++;
    if (advanced.month !== 'all') n++;
    if (advanced.supplier !== 'all') n++;
    if (advanced.currency !== 'all') n++;
    if (advanced.project !== 'all') n++;
    if (advanced.amountMin !== '') n++;
    if (advanced.amountMax !== '') n++;
    return n;
  }, [advanced]);

  const resetAllFilters = () => {
    setSearch('');
    setSourceFilter('all');
    setCategoryFilter('all');
    setAdvanced(defaultAdvanced);
  };

  // Build a plain object representing currently active filters — used by
  // the audit-share modal so the backend can replay the same query.
  const currentFilters = useMemo(() => {
    const out = {};
    if (sourceFilter !== 'all') out.source = sourceFilter;
    if (categoryFilter !== 'all') out.category = categoryFilter;
    if (search.trim()) out.search = search.trim();
    if (uploadedByParam) out.uploaded_by = uploadedByParam;
    if (advanced.dateFrom) out.date_from = advanced.dateFrom;
    if (advanced.dateTo) out.date_to = advanced.dateTo;
    if (advanced.year !== 'all') out.year = parseInt(advanced.year, 10);
    if (advanced.month !== 'all') out.month = parseInt(advanced.month, 10);
    if (advanced.supplier !== 'all') out.supplier = advanced.supplier;
    if (advanced.currency !== 'all') out.currency = advanced.currency;
    if (advanced.project !== 'all') out.project = advanced.project;
    if (advanced.amountMin !== '') out.amount_min = parseFloat(advanced.amountMin);
    if (advanced.amountMax !== '') out.amount_max = parseFloat(advanced.amountMax);
    return out;
  }, [sourceFilter, categoryFilter, search, uploadedByParam, advanced]);

  const fetchAuditHistory = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/documents/audit-shares`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.data?.success) {
        setAuditHistory(res.data.shares || []);
      }
    } catch (err) {
      console.error('audit history error', err);
    }
  };

  useEffect(() => {
    if (isAdminLike && showHistory) fetchAuditHistory();
  }, [isAdminLike, showHistory]);

  const fetchSchedules = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/documents/audit-share-schedules`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.data?.success) setSchedules(res.data.schedules || []);
    } catch (err) {
      console.error('schedules fetch error', err);
    }
  };

  useEffect(() => {
    if (isAdminLike && showSchedules) fetchSchedules();
  }, [isAdminLike, showSchedules]);

  const stopSchedule = async (id) => {
    if (!window.confirm('Bu sekansı durdurmak istediğinize emin misiniz?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/documents/audit-share-schedules/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Sekans durduruldu');
      fetchSchedules();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Sekans durdurulamadı');
    }
  };

  const handlePreview = (doc) => setPreviewItem(doc);

  const handleDownload = async (doc) => {
    try {
      if (doc.source === 'advance' && doc.download_url) {
        window.open(doc.download_url, '_blank');
        return;
      }
      const token = localStorage.getItem('token');
      const res = await axios.get(`${BACKEND_URL}${doc.download_url}`, {
        responseType: 'blob',
        headers: { Authorization: `Bearer ${token}` },
      });
      const blob = new Blob([res.data], { type: doc.file_type || 'application/octet-stream' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.name || 'belge';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Download error', err);
      toast.error('Belge indirilemedi');
    }
  };

  // Build user tabs list: "Herkes", "Kendi Belgelerim", plus each other uploader
  const userTabs = useMemo(() => {
    if (!isAdminLike) return [];
    const tabs = [
      { key: 'all', label: 'Herkes', icon: Users, count: null },
      { key: 'me', label: 'Kendi Belgelerim', icon: UserIcon, count: null },
    ];
    const others = (facets.uploaders || []).filter((u) => u.id !== user?.id);
    others.forEach((u) => {
      tabs.push({ key: u.id, label: u.name, icon: UserIcon, count: u.count });
    });
    return tabs;
  }, [isAdminLike, facets.uploaders, user]);

  return (
    <div data-testid="unified-documents-list" className="space-y-4">
      {/* User-scope tabs (Süper Admin / Admin only) */}
      {isAdminLike && userTabs.length > 0 && (
        <div data-testid="user-tabs" className="flex items-center gap-1 overflow-x-auto pb-2 border-b border-gray-200">
          {userTabs.map((tab) => (
            <button
              key={tab.key}
              data-testid={`user-tab-${tab.key}`}
              onClick={() => setUserTab(tab.key)}
              className={`inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-t-md whitespace-nowrap transition-colors border-b-2 -mb-px ${
                userTab === tab.key
                  ? 'border-blue-500 text-blue-700 bg-blue-50'
                  : 'border-transparent text-gray-600 hover:text-gray-900 hover:bg-gray-50'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
              {tab.count !== null && tab.count !== undefined ? (
                <span className="ml-1 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 bg-gray-100 text-gray-700 text-xs rounded-full">
                  {tab.count}
                </span>
              ) : null}
            </button>
          ))}
        </div>
      )}

      {/* Header counts */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="px-3 py-1.5 rounded-full bg-gray-100 text-gray-700 text-sm font-medium">
          Sonuç: <span data-testid="unified-count-total" className="font-bold">{counts.total}</span>
          {totalUnfiltered > counts.total ? (
            <span className="text-gray-400 ml-1">/ {totalUnfiltered}</span>
          ) : null}
        </div>
        <div className="px-3 py-1.5 rounded-full bg-sky-50 text-sky-700 text-sm font-medium border border-sky-200">
          Manuel: <span data-testid="unified-count-manual" className="font-bold">{counts.manual}</span>
        </div>
        <div className="px-3 py-1.5 rounded-full bg-amber-50 text-amber-700 text-sm font-medium border border-amber-200">
          Avans: <span data-testid="unified-count-advance" className="font-bold">{counts.advance}</span>
        </div>
        {(activeAdvancedCount > 0 || search || sourceFilter !== 'all' || categoryFilter !== 'all') && (
          <button
            data-testid="unified-reset-filters"
            onClick={resetAllFilters}
            className="inline-flex items-center gap-1 text-xs text-red-600 hover:text-red-700 font-medium"
          >
            <X className="w-3.5 h-3.5" /> Filtreleri temizle
          </button>
        )}
        <button
          data-testid="unified-refresh-btn"
          onClick={fetchDocs}
          className="ml-auto inline-flex items-center gap-1.5 text-sm text-gray-600 hover:text-gray-900"
        >
          <RefreshCw className="w-4 h-4" /> Yenile
        </button>
        {isAdminLike && (
          <>
            <button
              data-testid="audit-share-btn"
              onClick={() => setShowAuditModal(true)}
              disabled={documents.length === 0}
              className="inline-flex items-center gap-1.5 text-sm font-medium px-3 py-1.5 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              title="Filtrelenmiş belgeleri ZIP olarak denetçiye e-postala"
            >
              <Send className="w-4 h-4" /> Denetçiye Gönder
            </button>
            <button
              data-testid="audit-history-toggle"
              onClick={() => setShowHistory((v) => !v)}
              className={`inline-flex items-center gap-1.5 text-sm font-medium px-3 py-1.5 rounded-md border ${
                showHistory ? 'bg-gray-100 border-gray-300' : 'border-gray-200 hover:bg-gray-50'
              }`}
              title="Paylaşım geçmişi"
            >
              <History className="w-4 h-4" />
            </button>
            <button
              data-testid="audit-schedules-toggle"
              onClick={() => setShowSchedules((v) => !v)}
              className={`inline-flex items-center gap-1.5 text-sm font-medium px-3 py-1.5 rounded-md border ${
                showSchedules ? 'bg-indigo-100 border-indigo-300 text-indigo-700' : 'border-gray-200 hover:bg-gray-50'
              }`}
              title="Sekans paylaşımları"
            >
              🔁 Sekanslar
            </button>
          </>
        )}
      </div>

      {/* Audit share history */}
      {isAdminLike && showHistory && (
        <div data-testid="audit-history" className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <div className="px-4 py-2 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
            <h4 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
              <History className="w-4 h-4" /> Denetçi Paylaşım Geçmişi
            </h4>
            <span className="text-xs text-gray-500">{auditHistory.length} kayıt</span>
          </div>
          {auditHistory.length === 0 ? (
            <div className="text-center py-6 text-sm text-gray-500">
              Henüz paylaşım yapılmadı.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 text-gray-600 uppercase text-xs">
                  <tr>
                    <th className="px-4 py-2 text-left">Tarih</th>
                    <th className="px-4 py-2 text-left">Başlık</th>
                    <th className="px-4 py-2 text-left">Alıcı</th>
                    <th className="px-4 py-2 text-left">Belge</th>
                    <th className="px-4 py-2 text-left">Boyut</th>
                    <th className="px-4 py-2 text-left">Durum</th>
                    <th className="px-4 py-2 text-right">İşlem</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {auditHistory.map((s) => (
                    <tr key={s.id} data-testid={`audit-history-row-${s.id}`}>
                      <td className="px-4 py-2 whitespace-nowrap text-gray-700">
                        {new Date(s.created_at).toLocaleDateString('tr-TR')}
                      </td>
                      <td className="px-4 py-2 text-gray-900">{s.share_label || '—'}</td>
                      <td className="px-4 py-2">
                        <div className="text-gray-900">{s.recipient_email}</div>
                        {s.recipient_name && <div className="text-xs text-gray-500">{s.recipient_name}</div>}
                      </td>
                      <td className="px-4 py-2 text-gray-700">{s.doc_count}</td>
                      <td className="px-4 py-2 text-gray-700">
                        {((s.size_bytes || 0) / 1024 / 1024).toFixed(2)} MB
                      </td>
                      <td className="px-4 py-2">
                        {s.is_active ? (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-200">
                            Aktif
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600 border border-gray-200">
                            Süresi doldu
                          </span>
                        )}
                        <div className="text-xs text-gray-400 mt-0.5">
                          {s.email_status === 'sent' ? '✉ gönderildi' : `✉ ${s.email_status}`}
                        </div>
                      </td>
                      <td className="px-4 py-2 text-right">
                        {s.is_active && s.download_url ? (
                          <div className="inline-flex items-center gap-1">
                            <a
                              data-testid={`audit-history-open-${s.id}`}
                              href={s.download_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="p-1.5 rounded hover:bg-blue-100 text-blue-600"
                              title="Linki aç"
                            >
                              <ExternalLink className="w-4 h-4" />
                            </a>
                            <button
                              data-testid={`audit-history-copy-${s.id}`}
                              onClick={() => {
                                navigator.clipboard.writeText(s.download_url);
                                toast.success('Link kopyalandı');
                              }}
                              className="p-1.5 rounded hover:bg-gray-100 text-gray-600"
                              title="Linki kopyala"
                            >
                              <Copy className="w-4 h-4" />
                            </button>
                          </div>
                        ) : (
                          <span className="text-xs text-gray-400">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Schedules table */}
      {isAdminLike && showSchedules && (
        <div data-testid="audit-schedules" className="bg-white border border-indigo-200 rounded-lg overflow-hidden">
          <div className="px-4 py-2 bg-indigo-50 border-b border-indigo-200 flex items-center justify-between">
            <h4 className="text-sm font-semibold text-indigo-900 flex items-center gap-2">
              🔁 Aktif Sekanslar (Otomatik Paylaşımlar)
            </h4>
            <span className="text-xs text-indigo-700">{schedules.length} sekans</span>
          </div>
          {schedules.length === 0 ? (
            <div className="text-center py-6 text-sm text-gray-500">
              Henüz sekans kurulmadı. &ldquo;Denetçiye Gönder&rdquo; modalında &ldquo;Sekans Kur&rdquo; seçeneği ile başlatabilirsiniz.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 text-gray-600 uppercase text-xs">
                  <tr>
                    <th className="px-4 py-2 text-left">Periyot</th>
                    <th className="px-4 py-2 text-left">Alıcı</th>
                    <th className="px-4 py-2 text-left">CC</th>
                    <th className="px-4 py-2 text-left">Sonraki Gönderim</th>
                    <th className="px-4 py-2 text-left">Son Gönderim</th>
                    <th className="px-4 py-2 text-left">Gönderim</th>
                    <th className="px-4 py-2 text-right">İşlem</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {schedules.map((s) => (
                    <tr key={s.id} data-testid={`audit-schedule-row-${s.id}`}>
                      <td className="px-4 py-2 whitespace-nowrap">
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-50 text-indigo-700 border border-indigo-200">
                          {s.cadence === 'monthly' ? 'Her Ay' : s.cadence === 'quarterly' ? 'Her Çeyrek' : 'Her Yıl'}
                        </span>
                      </td>
                      <td className="px-4 py-2">
                        <div className="text-gray-900">{s.recipient_email}</div>
                        {s.recipient_name && <div className="text-xs text-gray-500">{s.recipient_name}</div>}
                      </td>
                      <td className="px-4 py-2 text-xs text-gray-600">
                        {(s.cc_emails || []).length === 0 ? <span className="text-gray-400">—</span> :
                          (s.cc_emails || []).join(', ')}
                      </td>
                      <td className="px-4 py-2 text-gray-700 whitespace-nowrap">
                        {s.next_run_at ? new Date(s.next_run_at).toLocaleDateString('tr-TR') : '—'}
                      </td>
                      <td className="px-4 py-2 text-gray-700 whitespace-nowrap">
                        {s.last_run_at ? new Date(s.last_run_at).toLocaleDateString('tr-TR') : <span className="text-gray-400">Henüz yok</span>}
                      </td>
                      <td className="px-4 py-2 text-gray-700">{s.runs_count || 0} kez</td>
                      <td className="px-4 py-2 text-right">
                        <button
                          data-testid={`audit-schedule-stop-${s.id}`}
                          onClick={() => stopSchedule(s.id)}
                          className="text-xs px-2 py-1 rounded-md bg-red-50 text-red-700 hover:bg-red-100 border border-red-200 font-medium"
                        >
                          Durdur
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Primary filters */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            data-testid="unified-search-input"
            type="text"
            placeholder="Belge adı, işletme veya açıklama ara..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 bg-white"
          />
        </div>

        <div className="relative">
          <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <select
            data-testid="unified-source-filter"
            value={sourceFilter}
            onChange={(e) => setSourceFilter(e.target.value)}
            className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 bg-white"
          >
            <option value="all">Tüm Kaynaklar</option>
            <option value="advance">Sadece Avans Kapama</option>
            <option value="manual">Sadece Manuel</option>
          </select>
        </div>

        <div className="relative">
          <Tag className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <select
            data-testid="unified-category-filter"
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 bg-white"
          >
            <option value="all">Tüm Kategoriler</option>
            {facets.categories.map((c) => (
              <option key={c.name} value={c.name}>
                {c.name} ({c.count})
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Advanced toggle */}
      <div className="flex items-center justify-between">
        <button
          data-testid="unified-toggle-advanced"
          onClick={() => setShowAdvanced((v) => !v)}
          className={`inline-flex items-center gap-2 text-sm font-medium px-3 py-1.5 rounded-md transition-colors ${
            showAdvanced ? 'bg-blue-50 text-blue-700 border border-blue-200' : 'text-gray-700 hover:bg-gray-100 border border-gray-200'
          }`}
        >
          <SlidersHorizontal className="w-4 h-4" />
          Gelişmiş Filtreler
          {activeAdvancedCount > 0 ? (
            <span className="ml-1 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 bg-blue-600 text-white text-xs rounded-full">
              {activeAdvancedCount}
            </span>
          ) : null}
        </button>
      </div>

      {/* Advanced panel */}
      {showAdvanced && (
        <div
          data-testid="unified-advanced-panel"
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 p-4 bg-blue-50/40 rounded-lg border border-blue-200"
        >
          {/* Year */}
          <div>
            <label className="text-xs font-medium text-gray-600 mb-1 flex items-center gap-1">
              <Calendar className="w-3.5 h-3.5" /> Yıl
            </label>
            <select
              data-testid="unified-filter-year"
              value={advanced.year}
              onChange={(e) => setAdvanced((a) => ({ ...a, year: e.target.value }))}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 bg-white"
            >
              <option value="all">Tüm Yıllar</option>
              {(() => {
                const counts = new Map((facets.years || []).map((y) => [String(y.name), y.count]));
                const nowY = new Date().getFullYear();
                const range = new Set();
                for (let y = nowY + 1; y >= nowY - 4; y--) range.add(y);
                (facets.years || []).forEach((y) => range.add(Number(y.name)));
                const sorted = Array.from(range).sort((a, b) => b - a);
                return sorted.map((y) => {
                  const c = counts.get(String(y));
                  return (
                    <option key={y} value={y}>
                      {y}{c ? ` (${c})` : ''}
                    </option>
                  );
                });
              })()}
            </select>
          </div>

          {/* Month */}
          <div>
            <label className="text-xs font-medium text-gray-600 mb-1 flex items-center gap-1">
              <Calendar className="w-3.5 h-3.5" /> Ay
            </label>
            <select
              data-testid="unified-filter-month"
              value={advanced.month}
              onChange={(e) => setAdvanced((a) => ({ ...a, month: e.target.value }))}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 bg-white"
            >
              <option value="all">Tüm Aylar</option>
              {(() => {
                const counts = new Map((facets.months || []).map((m) => [Number(m.name), m.count]));
                return Array.from({ length: 12 }, (_, i) => i + 1).map((m) => {
                  const c = counts.get(m);
                  return (
                    <option key={m} value={m}>
                      {TR_MONTHS[m]}{c ? ` (${c})` : ''}
                    </option>
                  );
                });
              })()}
            </select>
          </div>

          {/* Date range */}
          <div>
            <label className="text-xs font-medium text-gray-600 mb-1 flex items-center gap-1">
              <Calendar className="w-3.5 h-3.5" /> Başlangıç Tarihi
            </label>
            <input
              data-testid="unified-filter-date-from"
              type="date"
              value={advanced.dateFrom}
              onChange={(e) => setAdvanced((a) => ({ ...a, dateFrom: e.target.value }))}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 bg-white"
            />
          </div>
          <div>
            <label className="text-xs font-medium text-gray-600 mb-1 flex items-center gap-1">
              <Calendar className="w-3.5 h-3.5" /> Bitiş Tarihi
            </label>
            <input
              data-testid="unified-filter-date-to"
              type="date"
              value={advanced.dateTo}
              onChange={(e) => setAdvanced((a) => ({ ...a, dateTo: e.target.value }))}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 bg-white"
            />
          </div>

          {/* Supplier */}
          <div>
            <label className="text-xs font-medium text-gray-600 mb-1 flex items-center gap-1">
              <UserIcon className="w-3.5 h-3.5" /> İşletme
            </label>
            <select
              data-testid="unified-filter-supplier"
              value={advanced.supplier}
              onChange={(e) => setAdvanced((a) => ({ ...a, supplier: e.target.value }))}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 bg-white"
            >
              <option value="all">Tüm İşletmeler</option>
              {facets.suppliers.map((s) => (
                <option key={s.name} value={s.name}>
                  {s.name} ({s.count})
                </option>
              ))}
            </select>
          </div>

          {/* Currency */}
          <div>
            <label className="text-xs font-medium text-gray-600 mb-1 flex items-center gap-1">
              <Coins className="w-3.5 h-3.5" /> Para Birimi
            </label>
            <select
              data-testid="unified-filter-currency"
              value={advanced.currency}
              onChange={(e) => setAdvanced((a) => ({ ...a, currency: e.target.value }))}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 bg-white"
            >
              <option value="all">Tüm Para Birimleri</option>
              {facets.currencies.map((c) => (
                <option key={c.name} value={c.name}>
                  {c.name} ({c.count})
                </option>
              ))}
            </select>
          </div>

          {/* Project */}
          <div>
            <label className="text-xs font-medium text-gray-600 mb-1 flex items-center gap-1">
              <Briefcase className="w-3.5 h-3.5" /> Proje
            </label>
            <select
              data-testid="unified-filter-project"
              value={advanced.project}
              onChange={(e) => setAdvanced((a) => ({ ...a, project: e.target.value }))}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 bg-white"
            >
              <option value="all">Tüm Projeler</option>
              {facets.projects.map((p) => (
                <option key={p.name} value={p.name}>
                  {p.name} ({p.count})
                </option>
              ))}
            </select>
          </div>

          {/* Amount range */}
          <div>
            <label className="text-xs font-medium text-gray-600 mb-1 flex items-center gap-1">
              <DollarSign className="w-3.5 h-3.5" /> Min. Tutar
            </label>
            <input
              data-testid="unified-filter-amount-min"
              type="number"
              min="0"
              step="0.01"
              placeholder="0"
              value={advanced.amountMin}
              onChange={(e) => setAdvanced((a) => ({ ...a, amountMin: e.target.value }))}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 bg-white"
            />
          </div>
          <div>
            <label className="text-xs font-medium text-gray-600 mb-1 flex items-center gap-1">
              <DollarSign className="w-3.5 h-3.5" /> Max. Tutar
            </label>
            <input
              data-testid="unified-filter-amount-max"
              type="number"
              min="0"
              step="0.01"
              placeholder="∞"
              value={advanced.amountMax}
              onChange={(e) => setAdvanced((a) => ({ ...a, amountMax: e.target.value }))}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 bg-white"
            />
          </div>
        </div>
      )}

      {/* List */}
      {loading ? (
        <div className="flex items-center justify-center py-12 text-gray-500">
          <Loader2 className="w-5 h-5 animate-spin mr-2" /> Yükleniyor...
        </div>
      ) : documents.length === 0 ? (
        <div data-testid="unified-empty-state" className="text-center py-12 bg-white border border-dashed border-gray-300 rounded-lg">
          <FileText className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-600 font-medium">Filtrelere uyan belge bulunamadı</p>
          <p className="text-sm text-gray-500 mt-1">
            Filtreleri temizleyerek tüm belgeleri görebilirsiniz. Avans belgeleri
            avans kapama sırasında yüklediğiniz makbuzlardan otomatik gelir.
          </p>
        </div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 text-gray-600 uppercase text-xs tracking-wide">
                <tr>
                  <th className="px-4 py-3 text-left">Belge Adı</th>
                  <th className="px-4 py-3 text-left">Kaynak</th>
                  <th className="px-4 py-3 text-left">Tarih</th>
                  <th className="px-4 py-3 text-left">Kategori</th>
                  <th className="px-4 py-3 text-right">Tutar</th>
                  <th className="px-4 py-3 text-right">İşlem</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {documents.map((d) => (
                  <tr
                    key={`${d.source}-${d.id}`}
                    data-testid={`unified-row-${d.id}`}
                    className="hover:bg-blue-50/40 transition-colors"
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-start gap-2">
                        <FileText className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                        <div className="min-w-0">
                          <div className="font-medium text-gray-900 truncate max-w-xs" title={d.name}>
                            {d.name}
                          </div>
                          {d.supplier ? (
                            <div className="text-xs text-gray-500 truncate max-w-xs">
                              {d.supplier}
                            </div>
                          ) : null}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span
                        data-testid={`unified-source-badge-${d.id}`}
                        className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${sourceBadge(d.source)}`}
                        title={d.source_label}
                      >
                        {d.source === 'advance' ? 'Avans' : 'Manuel'}
                      </span>
                      {d.source === 'advance' && d.source_label && d.source_label !== 'Avans' ? (
                        <div className="text-xs text-gray-500 mt-1 truncate max-w-[160px]" title={d.source_label}>
                          {d.source_label.replace(/^Avans\s—\s/, '')}
                        </div>
                      ) : null}
                      {d.project ? (
                        <div className="text-xs text-gray-400 mt-0.5 truncate max-w-[160px]" title={d.project}>
                          {d.project}
                        </div>
                      ) : null}
                    </td>
                    <td className="px-4 py-3 text-gray-700 whitespace-nowrap">
                      <div className="flex items-center gap-1">
                        <Calendar className="w-3.5 h-3.5 text-gray-400" />
                        {formatDate(d.date)}
                      </div>
                      {d.uploaded_by_name ? (
                        <div className="text-xs text-gray-400 flex items-center gap-1 mt-0.5">
                          <UserIcon className="w-3 h-3" />
                          {d.uploaded_by_name}
                        </div>
                      ) : null}
                    </td>
                    <td className="px-4 py-3">
                      {d.category ? (
                        <div>
                          <div className="text-gray-800">{d.category}</div>
                          {d.subcategory ? (
                            <div className="text-xs text-gray-500">{d.subcategory}</div>
                          ) : null}
                        </div>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right font-medium text-gray-900 whitespace-nowrap">
                      {formatAmount(d.amount, d.currency)}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex items-center gap-1">
                        <button
                          data-testid={`unified-preview-btn-${d.id}`}
                          onClick={() => handlePreview(d)}
                          className="p-1.5 rounded hover:bg-blue-100 text-blue-600"
                          title="Önizle"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button
                          data-testid={`unified-download-btn-${d.id}`}
                          onClick={() => handleDownload(d)}
                          className="p-1.5 rounded hover:bg-emerald-100 text-emerald-600"
                          title="İndir"
                        >
                          <Download className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {previewItem && (
        <DocumentPreviewModal
          isOpen={!!previewItem}
          onClose={() => setPreviewItem(null)}
          document={{
            id: previewItem.id,
            file_name: previewItem.name,
            file_type: previewItem.file_type,
            file_extension: previewItem.file_extension,
            fileUrl: previewItem.preview_url || previewItem.url,
            url: previewItem.preview_url || previewItem.url,
            supplier_name: previewItem.supplier,
            amount: previewItem.amount,
            currency_code: previewItem.currency,
            description: previewItem.description,
            uploaded_at: previewItem.date,
            uploaded_by_name: previewItem.uploaded_by_name,
            expense_category_name: previewItem.category,
            expense_subcategory_name: previewItem.subcategory,
          }}
        />
      )}

      <AuditShareModal
        open={showAuditModal}
        onClose={() => setShowAuditModal(false)}
        filters={currentFilters}
        documentCount={documents.length}
        onShared={(payload) => {
          if (showHistory) fetchAuditHistory();
          if (payload?.schedule && showSchedules) fetchSchedules();
        }}
      />
    </div>
  );
};

export default UnifiedDocumentsList;
